import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { NotFound } from './components/NotFound/NotFound.tsx';
import './index.css';
import * as Sentry from '@sentry/react';
import { AnimatedLogo } from './components/AnimatedLogo/AnimatedLogo.tsx';
import { ErrorPage } from './components/ErrorPage/ErrorPage.tsx';
import { FeatureFlagProvider } from './FeatureFlagProvider.tsx';
import {
  apiBaseUrl,
  isProd,
  sentryDsn,
  sentryReplaysOnErrorSampleRate,
  sentryReplaysSessionSampleRate,
  sentryTracesSampleRate,
} from './lib/config.ts';
import { logConsoleWarning } from './lib/consoleWarning.ts';
import { polyfillNumberFormat } from './lib/intlPolyfill.ts';
import { tryCatchP } from './lib/tryCatch.ts';
import { routeTree } from './routeTree.gen';
import { queryClient, Web3Provider } from './Web3Provider.tsx';

export const router = createRouter({
  defaultOnCatch(error, errorInfo) {
    Sentry.captureReactException(error, errorInfo);
  },
  routeTree,
  defaultErrorComponent: ({ error }: { error: Error }) => <ErrorPage error={error} />,
  defaultPendingComponent: () => (
    <div className="flex h-full min-h-0 grow items-center justify-center">
      <AnimatedLogo className="size-8 text-contentInverseSecondary" size={40} />
    </div>
  ),
  defaultNotFoundComponent: NotFound,
  // If 'intent', routes will be preloaded by default when the user hovers over a link or a touchstart event is detected on a <Link>.
  defaultPreload: 'intent',
  context: { queryClient },
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  trailingSlash: 'never',
});

Sentry.init({
  enabled: Boolean(isProd && sentryDsn),
  dsn: sentryDsn,
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    Sentry.feedbackIntegration({
      showBranding: false,
      colorScheme: 'dark',
      autoInject: false,
    }),
  ],
  // Tracing
  tracesSampleRate: sentryTracesSampleRate,
  tracePropagationTargets: [apiBaseUrl],
  // Session Replay
  replaysSessionSampleRate: sentryReplaysSessionSampleRate,
  replaysOnErrorSampleRate: sentryReplaysOnErrorSampleRate,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
// Load polyfill in the background (if needed).
void tryCatchP(polyfillNumberFormat);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Web3Provider>
      <FeatureFlagProvider>
        <RouterProvider router={router} />
        <ReactQueryDevtools buttonPosition="bottom-left" />
      </FeatureFlagProvider>
    </Web3Provider>
  </React.StrictMode>
);

// Display a warning in the console against pasting code.
if (isProd) {
  logConsoleWarning();
}
